// Copyright (c) 2016 Deere & Company

import Claim, {getDefaultState as getClaimDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-part';
import {addEmptyClaimRows, removeEmptyRows} from '../../shared/services/empty-claim-rows';
import {
    findMatchingAccount,
    findMatchingKey,
    getPartUnit
} from '../../shared/services/claim-shared-helper';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimComplaintCause from '@deere/cps-it.warranty-model-factory/lib/claim-complaint-cause';
import actions from '../../action-list';
import {getDefaultState as getClaimPartDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-part';
import {getDefaultState} from '../state';
import {getDefaultState as getLaborDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-labor';
import {getDefaultState as getOtherCreditDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-other-credit';

const setPartsClaim = (state, action) =>
    Claim.update(state, {
        $set: action.updatedClaim
    });

const updateClaimMode = (state, action) =>
    Claim.update(state, {
        $set: action.isEditable ?
            addEmptyClaimRows(state, Claim) :
            removeEmptyRows(state, Claim)
    });

const copyClaim = state => {

    const defaultClaim = getClaimDefaultState();

    return Claim.update(state, {
        $merge: {
            allowedActions: [
                new ClaimAction({
                    actionName: 'DRAFT',
                    id: 'DRAFT',
                    mode: 'CHG'
                }),
                new ClaimAction({
                    actionName: 'SAVE_VALD',
                    id: 'SAVE_VALD',
                    mode: 'CHG'
                })
            ],
            claimNumber: defaultClaim.claimNumber,
            claimStatus: defaultClaim.claimStatus,
            claimValueDetails: defaultClaim.claimValueDetails,
            complaintCauseCommentsBasis: new ClaimComplaintCause({
                ...state.complaintCauseCommentsBasis,
                comments: defaultClaim.complaintCauseCommentsBasis.comments
            }),
            errors: defaultClaim.errors,
            recordInfo: defaultClaim.recordInfo,
            reimbursementDetails: defaultClaim.reimbursementDetails

        }
    });

};

const updatePartProperty = (state, action) =>
    Claim.update(state, {
        parts: {
            [action.index]: {
                [action.property]: {
                    $set: action.data
                }
            }
        }
    });

const updatePartsUnit = (state, action) =>
    Claim.update(state, {
        parts: {
            [action.index]: {
                unit: {
                    $set: action.data
                }
            }
        }
    });

const updateFailureDate = (state, action) =>
    Claim.update(state, {
        failureInfo: {
            failureDate: {
                $set: action.failureDate
            }
        }
    });

const additionalRepair = (state, action) => {

    const defaultClaim = getClaimDefaultState();

    return Claim.update(defaultClaim, {
        $merge: {
            failureInfo: {
                ...defaultClaim.failureInfo,
                failureDate: action.data.failureInfo.failureDate,
                repairDate: action.data.failureInfo.repairDate
            },
            pin: action.data.pin,
            referenceInfo: {
                ...defaultClaim.referenceInfo,
                ownershipStatus: action.data.referenceInfo.ownershipStatus,
                referenceNumber1: action.data.referenceInfo.referenceNumber1
            },
            responsibleAccount: action.data.responsibleAccount,
            useAmounts: action.data.useAmounts
        }
    });

};

const updateRepairDate = (state, action) =>
    Claim.update(state, {
        failureInfo: {
            repairDate: {
                $set: action.repairDate
            }
        }
    });

const updateFailurePart = (state, action) =>
    Claim.update(state, {
        failureInfo: {
            failurePartNumber: {
                $set: action.partNumber
            }
        }
    });

const updateWorkOrderTwo = (state, action) =>
    Claim.update(state, {
        referenceInfo: {
            referenceNumber2: {
                $set: action.workOrder
            }
        }
    });

const updateRepairNumber = (state, action) =>
    Claim.update(state, {
        referenceInfo: {
            repairNumber: {
                $set: action.repairNumber
            }
        }
    });

const updateWorkOrderOne = (state, action) =>
    Claim.update(state, {
        referenceInfo: {
            referenceNumber1: {
                $set: action.workOrder
            }
        }
    });

const updateClaimProperty = (state, action) =>
    Claim.update(state, {
        [action.property]: {
            $set: action.value
        }
    });

const updateResponsibleAccounts = (state, action) =>
    Claim.update(state, {
        responsibleAccount: {
            $set: findMatchingAccount(action.accountId, action.dealerCollection)
        }
    });

const updateInstalledBy = (state, action) =>
    Claim.update(state, {
        installedBy: {
            $set: findMatchingKey(action.key, action.installedByCollection)

        }
    });

const updateOwnershipStatus = (state, action) =>
    Claim.update(state, {
        referenceInfo: {
            ownershipStatus: {
                $set: findMatchingKey(action.key, action.statusCollection)
            }
        }
    });

const updatePartDetails = (state, action) =>
    Claim.update(state, {
        parts: {
            [action.index]: {
                $merge: {
                    partDescription: action.partDetails.partDescription,
                    partNumber: action.partDetails.partNumber,
                    unit: getPartUnit(state.parts[action.index].unit, action.partDetails.unit[0])
                }
            }
        }
    });

const updateUseAmount = (state, action) =>
    Claim.update(state, {
        useAmounts: {
            [action.index]: {
                amount: {
                    $set: action.amount
                }
            }
        }
    });

const updateUseAmounts = (state, action) =>
    Claim.update(state, {
        useAmounts: {
            $set: action.useAmounts
        }
    });

const updateUseAmountOnProductLoad = (state, action) =>
    Claim.update(state, {
        useAmounts: {
            $push: [action.useAmount]
        }
    });

const updatePartUseAmount = (state, action) =>
    Claim.update(state, {
        partUseAmount: {
            amount: {
                $set: action.amount
            }
        }
    });

const updateUnitDropDown = (state, action) =>
    Claim.update(state, {
        useAmounts: {
            0: {
                unit: {
                    $set: findMatchingKey(action.key, action.unitCollection)
                }
            }
        }
    });

const updatePartUseUnitDropDown = (state, action) =>
    Claim.update(state, {
        partUseAmount: {
            unit: {
                $set: findMatchingKey(action.key, action.unitCollection)
            }
        }
    });

const createLabor = state =>
    Claim.update(state, {
        labor: {
            $push: [getLaborDefaultState()]
        }
    });

const addPart = state =>
    Claim.update(state, {
        parts: {
            $push: [getClaimPartDefaultState()]
        }
    });

const deleteLabor = (state, action) =>
    Claim.update(state, {
        labor: {
            $splice: [[action.index, 1]]
        }
    });

const updateLaborProperty = (state, action) =>
    Claim.update(state, {
        labor: {
            [action.index]: {
                [action.property]: {
                    $set: action.data
                }
            }
        }
    });

const updateLaborType = (state, action) =>
    Claim.update(state, {
        labor: {
            [action.index]: {
                laborType: {
                    key: {
                        $set: action.laborType
                    }
                }
            }
        }
    });

const updateLaborLocation = (state, action) =>
    Claim.update(state, {
        labor: {
            [action.index]: {
                location: {
                    key: {
                        $set: action.location
                    }
                }
            }
        }
    });

const deletePart = (state, action) =>
    Claim.update(state, {
        parts: {
            $splice: [[action.index, 1]]
        }
    });

const updateCredits = (state, action) =>
    Claim.update(state, {
        otherCredits: {
            [action.index]: {
                [action.property]: {
                    $set: action.data
                }
            }
        }
    });

const createOtherCredit = state =>
    Claim.update(state, {
        otherCredits: {
            $push: [getOtherCreditDefaultState()]
        }
    });

const deleteOtherCredit = (state, action) =>
    Claim.update(state, {
        otherCredits: {
            $splice: [[action.index, 1]]
        }
    });

const updateFailureInfoDropDown = (state, action) =>
    Claim.update(state, {
        additionalFailureInfo: {
            [action.property]: {
                $set: findMatchingKey(action.key, action.collection)
            }
        }
    });

const updateFailureInfo = (state, action) =>
    Claim.update(state, {
        additionalFailureInfo: {
            [action.property]: {
                $set: action.value
            }
        }
    });

const resetPartsClaim = () =>
    getClaimDefaultState();

const resetUseAmounts = state =>
    Claim.update(state, {
        useAmounts: {
            $set: getDefaultState().claim.useAmounts
        }
    });

const updateComplaintCauseCorrection = (state, action) =>
    Claim.update(state, {
        complaintCauseCommentsBasis: {
            [action.property]: {
                $set: action.value
            }
        }
    });

const addFile = (state, action) =>
    Claim.update(state, {
        file: {
            $push: [action.data]
        }
    });

const deleteFile = (state, action) => {

    const index = state.file.findIndex(file => file.serialNumber === action.data.serialNumber && file.name === action.data.name);

    return Claim.update(state, {
        file: {
            $splice: [[index, 1]]
        }
    });

};

const updateFile = (state, action) =>
    Claim.update(state, {
        file: {
            $set: action.files
        }
    });

const updatePartType = (state, action) => {

    const resetPartTypes = Claim.update(state, {
        $merge: {
            lifeTime: false,
            merchandizeItem: false
        }
    });

    if (action.partType === '') {

        return resetPartTypes;

    }

    return Claim.update(resetPartTypes, {
        $merge: {
            [action.partType]: true,
            model: getDefaultState().claim.model,
            pin: getDefaultState().claim.pin,
            serialNumber: getDefaultState().claim.serialNumber
        }
    });

};

const updateGoodsInstallationVat = (state, action) =>
    Claim.update(state, {
        goodsInstallationVat: {
            $set: action.goodsInstallationVat
        }
    });

const revisePartsClaim = state =>
    Claim.update(state, {
        errors: {
            $set: []
        }
    });

export default (state = getDefaultState().claim, action) => {

    const actionTypes = {
        [actions.ADD_CLAIM_PARTS_ADDITIONAL_REPAIR]: additionalRepair,
        [actions.ADD_CLAIM_PARTS_FILE]: addFile,
        [actions.DELETE_CLAIM_PARTS_FILE]: deleteFile,
        [actions.COPY_CLAIM_PARTS]: copyClaim,
        [actions.CREATE_CLAIM_PARTS_OTHER_CREDIT]: createOtherCredit,
        [actions.DELETE_CLAIM_PARTS_OTHER_CREDIT]: deleteOtherCredit,
        [actions.UPDATE_CLAIM_PARTS_OTHER_CREDIT_DESCRIPTION]: updateCredits,
        [actions.UPDATE_CLAIM_PARTS_OTHER_CREDIT_AMOUNT]: updateCredits,
        [actions.UPDATE_CLAIM_PARTS_OTHER_CREDIT_DISTANCE]: updateCredits,
        [actions.UPDATE_CLAIM_PARTS_OTHER_CREDIT_COMMENTS]: updateCredits,
        [actions.CREATE_CLAIM_PARTS_LABOR]: createLabor,
        [actions.UPDATE_CLAIM_PARTS_LABOR_FIELDS]: updateLaborProperty,
        [actions.UPDATE_CLAIM_PARTS_LABOR_LOCATION]: updateLaborLocation,
        [actions.UPDATE_CLAIM_PARTS_LABOR_TYPE]: updateLaborType,
        [actions.DELETE_CLAIM_PARTS_LABOR]: deleteLabor,
        [actions.CREATE_CLAIM_PARTS_PART]: addPart,
        [actions.DELETE_CLAIM_PARTS_PART]: deletePart,
        [actions.RESET_CLAIM_PARTS]: resetPartsClaim,
        [actions.RESET_CLAIM_PARTS_USE_AMOUNTS]: resetUseAmounts,
        [actions.REVISE_CLAIM_PARTS]: revisePartsClaim,
        [actions.SET_CLAIM_PARTS]: setPartsClaim,
        [actions.SET_CLAIM_PARTS_PART_DETAILS]: updatePartDetails,
        [actions.UPDATE_CLAIM_PARTS_BASIS_FOR_SETTLEMENT]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_PARTS_COMPLAINT_CAUSE]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_PARTS_COMPLAINT_CORRECTION]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_PARTS_DOWN_TIME]: updateFailureInfoDropDown,
        [actions.UPDATE_CLAIM_PARTS_DTAC_CASE]: updateFailureInfo,
        [actions.UPDATE_CLAIM_PARTS_DTAC_SOLUTION]: updateFailureInfo,
        [actions.UPDATE_CLAIM_PARTS_INSTALLED_BY]: updateInstalledBy,
        [actions.UPDATE_CLAIM_PARTS_IS_EDITABLE]: updateClaimMode,
        [actions.UPDATE_CLAIM_PARTS_OPERATORS_COMPLAINT]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_PARTS_OPERATORS_COMMENTS]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_PARTS_OWNERSHIP_STATUS]: updateOwnershipStatus,
        [actions.UPDATE_CLAIM_PARTS_FAILED_AREA]: updateFailureInfo,
        [actions.UPDATE_CLAIM_PARTS_FAILURE_DATE]: updateFailureDate,
        [actions.UPDATE_CLAIM_PARTS_FAILED_MODE]: updateFailureInfoDropDown,
        [actions.UPDATE_CLAIM_PARTS_FAILED_MODE_COMMENTS]: updateFailureInfo,
        [actions.UPDATE_CLAIM_PARTS_FAILURE_PART]: updateFailurePart,
        [actions.UPDATE_CLAIM_PARTS_FILE]: updateFile,
        [actions.UPDATE_CLAIM_PARTS_PART_FIELDS]: updatePartProperty,
        [actions.UPDATE_CLAIM_PARTS_PART_SERIAL_NUMBER]: updateFailureInfo,
        [actions.UPDATE_CLAIM_PARTS_PART_UNIT]: updatePartsUnit,
        [actions.UPDATE_CLAIM_PARTS_PART_USE_AMOUNT]: updatePartUseAmount,
        [actions.UPDATE_CLAIM_PARTS_PART_USE_AMOUNT_UNIT]: updatePartUseUnitDropDown,
        [actions.UPDATE_CLAIM_PARTS_PROPERTY]: updateClaimProperty,
        [actions.UPDATE_CLAIM_PARTS_REPAIR_DATE]: updateRepairDate,
        [actions.UPDATE_CLAIM_PARTS_REPAIR_NUMBER]: updateRepairNumber,
        [actions.UPDATE_CLAIM_PARTS_RESPONSIBLE_ACCOUNTS]: updateResponsibleAccounts,
        [actions.UPDATE_CLAIM_PARTS_USE_AMOUNT]: updateUseAmount,
        [actions.UPDATE_CLAIM_PARTS_USE_AMOUNTS]: updateUseAmounts,
        [actions.UPDATE_CLAIM_PARTS_USE_AMOUNT_ON_PRODUCT_LOAD]: updateUseAmountOnProductLoad,
        [actions.UPDATE_CLAIM_PARTS_USE_AMOUNT_UNIT]: updateUnitDropDown,
        [actions.UPDATE_CLAIM_PARTS_WORK_ORDER_ONE]: updateWorkOrderOne,
        [actions.UPDATE_CLAIM_PARTS_WORK_ORDER_TWO]: updateWorkOrderTwo,
        [actions.UPDATE_CLAIM_PARTS_PART_TYPE]: updatePartType,
        [actions.UPDATE_CLAIM_PARTS_GOODS_INSTALLATION_VAT]: updateGoodsInstallationVat
    };

    const reducer = actionTypes[action.type];

    return reducer ?
        reducer(state, action) :
        state;

};



// WEBPACK FOOTER //
// ./src/claim/parts/reducers/parts-claim.js